import { format as formatDateFn, setHours, setMilliseconds, setMinutes, setSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { cs, enUS, sk } from 'date-fns/locale';
import type { Locale } from 'date-fns/locale';

import { ISO_DATE_TIME_REGEX, MONTH_YEAR_REGEX } from './regex';

export interface DateMask {
  Year: boolean;
  Month: boolean;
  Day: boolean;
  Time: boolean;
}

export const getDateMaskObject = (dateMask?: string): DateMask => {
  const dateMaskArray = dateMask ? dateMask.split(',').map((item) => item.trim()) : [];

  return {
    Year: dateMaskArray.includes('Year'),
    Month: dateMaskArray.includes('Month'),
    Day: dateMaskArray.includes('Day'),
    Time: dateMaskArray.includes('Time'),
  };
};

const localeMap: Record<string, Locale> = {
  cs: cs,
  en: enUS,
  sk: sk,
};

export const isDateValid = (date: string): boolean => {
  return !isNaN(new Date(date).getTime());
};

export const formatDate = (date: Date, format?: string, locale?: string): string => {
  const selectedLocale = locale && localeMap[locale] ? localeMap[locale] : undefined;
  return formatDateFn(date, format ? format : 'd.M.yyyy', { locale: selectedLocale });
};

export const normalizeToNoon = (date: Date) => {
  const localNoon = setMilliseconds(setSeconds(setMinutes(setHours(date, 12), 0), 0), 0);
  return toZonedTime(localNoon, 'Europe/Prague');
};

export const formatDateForInputs = (date: string | Date | null): string => {
  if (date === null) return '';

  return new Date(date).toISOString().split('T')[0];
};

export const formatDateTimeForInputs = (date?: string | Date | null): string => {
  if (!date || date === null) return '';

  return formatDateFn(new Date(date), "yyyy-MM-dd'T'HH:mm");
};

export const formatDateForBE = (dateInput: string | Date | null): string | null => {
  if (!dateInput) return null;
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return null;

  return date.toISOString();
};

export type DateFormatVariant = 'Date' | 'DateTime' | 'Time' | 'MonthYear' | null;

export const DATE_FORMAT_MAP = {
  Date: 'dd.MM.yyyy',
  DateTime: 'dd.MM.yyyy HH:mm',
  Time: 'HH:mm',
  MonthYear: 'MM/yyyy',
} satisfies Record<NonNullable<DateFormatVariant>, string>;

const getMonthYearDate = (value: string): Date | null => {
  const [yearValue, monthValue] = value.split('-');
  const year = Number(yearValue);
  const month = Number(monthValue);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, 1);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const formatHeliosDateValue = (value: string | null | undefined, variant?: DateFormatVariant, defaultFormat?: string): string | null => {
  if (!value) return null;

  if (MONTH_YEAR_REGEX.test(value)) {
    const parsedDate = getMonthYearDate(value);
    return parsedDate ? formatDateFn(parsedDate, 'MM/yyyy') : null;
  }

  if (!ISO_DATE_TIME_REGEX.test(value)) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const format = variant ? DATE_FORMAT_MAP[variant] : (defaultFormat ?? DATE_FORMAT_MAP.DateTime);
  return formatDateFn(parsedDate, format);
};

export const formatDateForBEByVariant = (dateInput: string | Date | null, variant?: DateFormatVariant): string | null => {
  if (!dateInput) return null;
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return null;

  if (variant === 'MonthYear') {
    return formatDateFn(date, 'yyyy-MM');
  }

  return date.toISOString();
};

export const getTimeFormat = (dateMask: DateMask | undefined): string => {
  return dateMask?.Time ? 'HH:mm' : '';
};

export const getDateFormat = (dateMask: DateMask | undefined): string => {
  const time = getTimeFormat(dateMask);

  const dateParts = [dateMask?.Day && 'dd.', dateMask?.Month && 'MM.', dateMask?.Year && 'yyyy'].filter(Boolean);

  const date = dateParts.join('');

  if (date && time) {
    return `${date} ${time}`;
  }
  return date || time;
};

export const formatDateWithMask = (date: Date | string, dateMask: string): string => {
  const mask = getDateMaskObject(dateMask);
  const dateFormat = getDateFormat(mask);
  return formatDate(new Date(date), dateFormat);
};

export const getDateFormatForDateMask = (dateMask?: string) => {
  if (!dateMask) return 'yyyy-MM-dd';

  const formatMap: Record<string, string> = {
    Year: 'yyyy',
    Month: 'MM',
    Day: 'dd',
    Time: 'HH:mm',
    'Month, Day': 'MM dd',
    'Month, Time': 'MM HH:mm',
    'Year, Month': 'yyyy MM',
    'Year, Day': 'yyyy dd',
    'Year, Time': 'yyyy HH:mm',
    'Year, Month, Day': 'yyyy MM dd',
    'Year, Month, Day, Time': 'yyyy MM dd HH:mm',
  };

  return formatMap[dateMask] || 'yyyy-MM-dd';
};

export const formatDateForBackend = (date: Date, dateMask?: string) => {
  if (!dateMask) return formatDateFn(date, 'yyyy-MM-dd');

  const formatMap: Record<string, string> = {
    Year: 'yyyy',
    Month: 'MM',
    Day: 'dd',
    Time: 'HH:mm',
    'Month, Day': 'MM-dd',
    'Month, Time': 'MM HH:mm',
    'Year, Month': 'yyyy-MM',
    'Year, Day': 'yyyy-dd',
    'Year, Time': 'yyyy HH:mm',
    'Year, Month, Day': 'yyyy-MM-dd',
    'Year, Month, Day, Time': 'yyyy-MM-dd HH:mm',
  };

  return formatDateFn(date, formatMap[dateMask] || 'yyyy-MM-dd');
};
